/**
 * Clay-art maps that live inside mobile components / modules rather than in
 * their own constants file (URL mirrors; files under public/icons/).
 */

/**
 * Tab bar art — mobile components/navigation/AnimatedTabBar.tsx `TAB_IMAGES`,
 * keyed by mobile route name (index = Diary). The art carries its own colour:
 * focus is conveyed by icon opacity (inactive 0.55) + the label colour, never
 * by tinting.
 */
export const TAB_IMAGES: Record<'index' | 'recipes' | 'progress' | 'settings', string> = {
  index: '/icons/tabs/tab-diary.png',
  recipes: '/icons/tabs/tab-recipes.png',
  progress: '/icons/tabs/tab-progress.png',
  settings: '/icons/tabs/tab-settings.png',
}

/** AnimatedTabBar constants that go with TAB_IMAGES. */
export const TAB_ICON_SIZE = 26
export const INACTIVE_ICON_OPACITY = 0.55

/** Tab label i18n keys (AnimatedTabBar `TAB_LABEL_KEYS`). */
export const TAB_LABEL_KEYS: Record<'index' | 'recipes' | 'progress' | 'settings', string> = {
  index: 'tabs.diary',
  recipes: 'tabs.recipes',
  progress: 'tabs.progress',
  settings: 'tabs.settings',
}

/** Onboarding answer art — mobile constants/onboarding.ts `ONBOARDING_IMAGES` (module-private there). */
export const ONBOARDING_IMAGES = {
  generalHealth: '/icons/onboarding/goal-general-health.png',
  justExploring: '/icons/onboarding/goal-just-exploring.png',
  control: '/icons/onboarding/motiv-control.png',
  energy: '/icons/onboarding/motiv-energy.png',
  health: '/icons/onboarding/motiv-health.png',
  consistency: '/icons/onboarding/struggle-consistency.png',
  eatingOut: '/icons/onboarding/struggle-eating-out.png',
  mealIdeas: '/icons/onboarding/struggle-meal-ideas.png',
  portionSizes: '/icons/onboarding/struggle-portion-sizes.png',
  readingLabels: '/icons/onboarding/struggle-reading-labels.png',
  home: '/icons/onboarding/style-home.png',
  mealPrep: '/icons/onboarding/style-meal-prep.png',
  mixed: '/icons/onboarding/style-mixed.png',
  tip: '/icons/analysis/section-tip.png',
} as const

/**
 * Recipe filter-chip and import-tab art. The PNGs ship in assets/icons/recipes
 * but NO mobile screen references them yet — mobile RecipeFilterChips uses
 * Ionicons (grid-outline, time-outline, bookmark-outline, flash-outline, …)
 * and ImportRecipeModal uses Ionicons tabs. For pixel parity follow mobile
 * (Ionicons); these maps exist so the art is addressable if mobile adopts it.
 * Keys are mobile's RecipeFilterCategory ids / ImportTab ids.
 */
export const RECIPE_CHIP_IMAGES: Record<'all' | 'new' | 'myrecipes' | 'quick' | 'dessert', string> = {
  all: '/icons/recipes/chip-all.png',
  new: '/icons/recipes/chip-new.png',
  myrecipes: '/icons/recipes/chip-myrecipes.png',
  quick: '/icons/recipes/chip-quick.png',
  dessert: '/icons/recipes/chip-dessert.png',
}

export const RECIPE_IMPORT_IMAGES: Record<'url' | 'photo' | 'pdf', string> = {
  url: '/icons/recipes/import-url.png',
  photo: '/icons/recipes/import-photo.png',
  pdf: '/icons/recipes/import-pdf.png',
}

/** Onboarding nutrient art — mobile constants/onboarding.ts `NUTRIENT_IMAGES` (module-private there). */
export const NUTRIENT_IMAGES = {
  addedSugar: '/nutrients/added-sugar.png',
} as const
